// The standard record UI (rules 1, 3, 10): one status pill, one editor action bar, one page
// action bar. A module passes labels and handlers; it does not draw its own.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon } from '../app/Icon';
import { stateOf, STATE_LABEL, STATE_PILL, type BaseRecord, type RecordState } from './engine';

/** Status pill. `labels` lets a sheet say Open/Closed; the colours never change. */
export function StatusPill({ record, labels }: { record: Pick<BaseRecord, 'status' | 'sentTo' | 'app'>; labels?: Partial<Record<RecordState, string>> }) {
  const st = stateOf(record);
  return <span className={`pill pill-nodot ${STATE_PILL[st]}`}>{labels?.[st] ?? STATE_LABEL[st]}</span>;
}

/** Editor bottom bar: Save and File. `fileLabel` is "File toolbox talk", "Close sheet for today", "Sign and file". */
export function DraftActions({ onSave, onFile, fileLabel, disabled, busy, fileIcon = 'sign' }: { onSave: () => void; onFile: () => void; fileLabel: string; disabled?: boolean; busy?: boolean; fileIcon?: string }) {
  return (
    <div className="fixed inset-x-0 bottom-[calc(76px_+_env(safe-area-inset-bottom,0px))] md:static p-3 bg-bg border-t border-line md:border-0 flex gap-2 print:hidden">
      <button className="btn" disabled={busy} onClick={onSave} aria-label="Save draft"><Icon name="check" size={18} />Save</button>
      <button className="btn btn-primary flex-1" disabled={busy || disabled} onClick={onFile}><Icon name={fileIcon} size={18} />{busy ? 'Filing…' : fileLabel}</button>
    </div>
  );
}

/**
 * Page bottom bar. A draft: Continue and Discard. Filed: PDF, Send to GC, and Void for the office.
 * Void and Discard confirm in place; nothing here deletes a filed record.
 */
export function RecordPageActions({ record, editorHref, onDiscard, onSend, onVoid, canVoid, sending, sendLabel, children }: {
  record: Pick<BaseRecord, 'status' | 'sentTo' | 'app'>; editorHref: string; onDiscard: () => Promise<void> | void;
  onSend?: () => void; onVoid?: (reason: string) => Promise<void> | void; canVoid?: boolean; sending?: boolean; sendLabel?: string; children?: ReactNode;
}) {
  const st = stateOf(record);
  const [confirm, setConfirm] = useState<'discard' | 'void' | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  async function run(fn: () => Promise<void> | void) { setBusy(true); try { await fn(); setConfirm(null); } finally { setBusy(false); } }
  return (
    <>
      <div className="fixed inset-x-0 bottom-[calc(76px_+_env(safe-area-inset-bottom,0px))] md:static p-3 bg-bg border-t border-line md:border-0 flex gap-2 print:hidden">
        {st === 'draft' ? (
          <>
            <button className="btn" disabled={busy} onClick={() => setConfirm('discard')}>Discard</button>
            <Link className="btn btn-primary flex-1" to={editorHref}><Icon name="chevron" size={18} />Continue</Link>
          </>
        ) : (
          <>
            <button className="btn flex-1" onClick={() => window.print()}><Icon name="print" size={18} />PDF</button>
            {onSend && <button className="btn flex-1" disabled={sending || st === 'void'} onClick={onSend}><Icon name="send" size={18} />{sending ? 'Sending…' : sendLabel ?? (st === 'sent' ? 'Send again' : 'Send to GC')}</button>}
            {canVoid && st !== 'void' && onVoid && <button className="btn" onClick={() => setConfirm('void')} aria-label="Void this record"><Icon name="warn" size={18} /></button>}
          </>
        )}
        {children}
      </div>
      {confirm && (
        <div className="fixed inset-0 z-40 flex items-end md:items-center md:justify-center print:hidden" style={{ background: 'var(--scrim, rgba(0,0,0,.5))' }} onClick={() => !busy && setConfirm(null)}>
          <div className="bg-surface w-full md:max-w-[460px] rounded-t-[22px] md:rounded-[18px] px-3.5 pt-3 pb-7 flex flex-col gap-2.5" style={{ paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))' }} onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 rounded bg-line mx-auto md:hidden" />
            {confirm === 'discard' ? (
              <>
                <h5 className="font-head font-bold text-[17px] m-0">Discard this draft?</h5>
                <p className="m-0 text-[13.5px] text-muted">It goes away for good, with any signatures taken on it. Nothing filed is affected.</p>
                <div className="flex gap-2"><button className="btn flex-1" disabled={busy} onClick={() => setConfirm(null)}>Keep it</button><button className="btn btn-primary flex-[2]" disabled={busy} onClick={() => run(onDiscard)}>{busy ? 'Discarding…' : 'Discard draft'}</button></div>
              </>
            ) : (
              <>
                <h5 className="font-head font-bold text-[17px] m-0">Void this record?</h5>
                <p className="m-0 text-[13.5px] text-muted">It stays on file marked Void with your name and the reason, and drops out of counts. It cannot be undone.</p>
                <input className="field-in" value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (required)" />
                <div className="flex gap-2"><button className="btn flex-1" disabled={busy} onClick={() => setConfirm(null)}>Cancel</button><button className="btn btn-primary flex-[2]" disabled={busy || !reason.trim()} onClick={() => run(() => onVoid!(reason))}>{busy ? 'Voiding…' : 'Void record'}</button></div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const ms = (v: unknown): number | null => { if (!v) return null; if (typeof v === 'number') return v; const x = v as { toMillis?: () => number; seconds?: number }; if (typeof x.toMillis === 'function') return x.toMillis(); if (typeof x.seconds === 'number') return x.seconds * 1000; return null; };
const stamp = (t: number) => new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/**
 * The Sent block (row 17): once a record went out, the page says to whom, when, by whom, how many
 * files, and opens the mail entry. Nothing until it has been sent.
 */
const str = (v: unknown): string | null => (typeof v === 'string' && v) ? v : null;
const cnt = (v: unknown): number => typeof v === 'number' && v > 0 ? v : 0;
// Any record shape (v1 records carry an open index signature, v2 ones are typed), coerced inside.
type Loose = Record<string, unknown>;
export function SentBlock({ record: rec, pid, label = 'Sent' }: { record: object; pid: string; label?: string }) {
  const record = rec as Loose;
  const to = str(record.sentTo); if (!to) return null;
  const at = ms(record.sentAt), by = str(record.sentByName), files = cnt(record.sentFiles), entry = str(record.sentEntryId);
  return (
    <div className="flex items-start gap-2.5 bg-ok-soft rounded-[12px] px-3 py-2.5 text-[13.5px]">
      <Icon name="send" size={18} className="text-ok shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <b className="font-head block text-ok">{label} to {to}</b>
        <span className="text-muted text-[12.5px]">{[at ? stamp(at) : null, by ? `by ${by}` : null, files ? `${files} file${files === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ')}</span>
      </div>
      {entry && <Link to={`/mail/${entry}?p=${pid}`} className="btn btn-sm shrink-0">In Mail</Link>}
    </div>
  );
}

/** The stamp (row 17): "Filed Oct 14, 3:05 PM by you" / "Saved by Matt". `me` is the signed-in name. */
export function Stamp({ record: rec, me }: { record: object; me?: string }) {
  const record = rec as Loose;
  const you = (v: unknown) => { const n = str(v); return n ? (me && n === me ? 'you' : n) : null; };
  const filedAt = ms(record.filedAt), updatedAt = ms(record.updatedAt), createdAt = ms(record.createdAt);
  const parts: string[] = [];
  if (record.status === 'filed' || record.status === 'void') { if (filedAt || str(record.filedByName)) parts.push(`Filed${filedAt ? ` ${stamp(filedAt)}` : ''}${you(record.filedByName) ? ` by ${you(record.filedByName)}` : ''}`); }
  if (updatedAt && (!filedAt || updatedAt > filedAt + 60_000)) parts.push(`Saved ${stamp(updatedAt)}${you(record.updatedByName) ? ` by ${you(record.updatedByName)}` : ''}`);
  if (!parts.length && (createdAt || str(record.createdByName))) parts.push(`Started${createdAt ? ` ${stamp(createdAt)}` : ''}${you(record.createdByName) ? ` by ${you(record.createdByName)}` : ''}`);
  if (!parts.length) return null;
  return <div className="text-muted text-[12px] font-head">{parts.join(' · ')}</div>;
}

/** The strip a voided record's page shows at the top. */
export function VoidBanner({ record }: { record: Pick<BaseRecord, 'status' | 'voidReason' | 'voidedBy'> }) {
  if (record.status !== 'void') return null;
  return <div className="flex items-start gap-2.5 bg-crit-soft rounded-[12px] px-3 py-2.5 text-[13.5px] print:border print:border-crit"><Icon name="warn" size={18} className="text-crit shrink-0 mt-0.5" /><div><b className="font-head block text-crit">Void</b>{record.voidReason}{record.voidedBy ? ` · ${record.voidedBy.name}` : ''}</div></div>;
}
